import { Injectable } from '@angular/core';
import { ConvertHelper, DotNetBridge } from '@barthauer/abc/core';
import { DataRowInfo, NetworkTraceModel, NetworkTraceObject } from '@barthauer/abc/core/data';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MapSelection } from '../map.selection';
import { MapView } from '../map.view';
import { ContextMenuConfiguration } from '../models/contextmenu.model';
import { DataEntity, DataEntityInfo, FeatureGeometryType } from '../models/element.model';
import { LayerItemFlatNode } from '../models/legend.model';
import { MapConfiguration } from '../models/map.config';
import { MapTarget } from '../models/map.model';
import { GeoLocationPosition, GeoLocationState, MapActionArgument, MapActionIds } from '../models/map.service.model';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

declare var ol: any;

interface GetDataEntityInfos {
  (entities: DataEntity[]): Observable<DataEntityInfo[]>;
}

interface GetQueryParameters {
  (): ParameterValue[];
}

interface GetAttributions {
  (): string[];
}

interface CheckItem {
  (name: string): boolean;
}

interface SupportsGeometryFunction {
  (pointsEnabled: boolean, linesEnabled: boolean, polygonsEnabled: boolean): void;
}

interface PrepareDataEntities {
  (entities: DataEntity[]): Observable<void>;
}

@Injectable()
export class MapService {
  private geoLocationStateSubject = new Subject<GeoLocationState>();
  private geoLocationPositionSubject = new Subject<GeoLocationPosition>();
  private geoLocation: any;
  private geoLocationPositionChangeEventKey: any;

  private mapToLayerSubject = new Subject<LayerItemFlatNode>();
  private mapOpenDataTableSubject = new Subject<LayerItemFlatNode>();

  private navigationSubject = new Subject<DataEntity[]>();
  private navigateToDataRowsSubject = new Subject<DataRowInfo[]>();
  private navigationSubjectWithoutZoom = new Subject<DataEntity[]>();
  private mapInitiatedSubject = new Subject<any>();
  private mapLoadedSubject = new Subject<MapSelection>();
  private mapShortMessageSubject = new Subject<string>();
  private loadMapConfigSubject = new Subject<MapConfiguration>();
  private mapRefreshSubject = new Subject<DataEntity[]>();
  private setCustomColorSubject = new Subject<{
    entities: DataEntity[];
    color: number[];
  }>();
  private mapActionSubject = new Subject<MapActionArgument>();
  private loadContextMenuConfigSubject = new Subject<ContextMenuConfiguration>();
  private pathInteractionSubject = new Subject<{
    entity: DataEntity;
    includeNodes: boolean;
  }>();
  private networkPathSubject = new Subject<DataEntity[]>();
  private networkTracingSubject = new Subject<string>();
  private enableRotationSubject = new Subject<boolean>();
  private extentSubject = new Subject<number[]>();
  private extentInteractionSubject = new Subject();
  private isLegendInitialized = false;
  private legendInitializedSubject = new Subject<void>();
  private isMobileEnvironmentSubject = new Subject<boolean>();
  private isFeatureSelectionCanceled = new Subject<boolean>();

  private mobileEnvironmentSubject = new BehaviorSubject<boolean>(false); //enable late subscriber to receive last value
  private mapSelectEntitiesSubject = new BehaviorSubject<(selection: DataEntity[]) => void>(undefined);
  private mapSelectEntitiesDefaultSubject = new BehaviorSubject<(selection: DataEntity[]) => void>(undefined);

  public targetButtonNames: string[] = [];

  public getWebApiBaseURL: () => string;
  public getDataEntityInfos: GetDataEntityInfos;
  public getTargetOfObjectSet: (objectSetName: string) => MapTarget;
  public getCurrentQueryParameters: GetQueryParameters;
  public canOpenDataTable: CheckItem;
  public supportsCreateGeometry: SupportsGeometryFunction;
  public getVisibleAttributions: GetAttributions;
  public prepareObjectGraphics: PrepareDataEntities;
  public getCurrentView: () => MapView;
  public saveLegendState: () => Observable<void>;
  public queryShortestPath: (model: NetworkTraceModel) => Observable<NetworkTraceObject[]>;
  public moveToNewCoordinate: (coordinate: number[]) => Promise<number[]>;
  public startInteractiveGeometryCapture: (FeatureGeometryType: FeatureGeometryType) => Promise<number[][]>;
  public enableContextMenu: (boolean) => void;
  public selectDataEntities: () => Promise<DataEntity[]>;
  public getVisibleSimpleLayerNames: () => Promise<string[]>;
  public executeCommand: (commandName: string, entites: DataEntity[]) => Promise<void>;

  private readonly geoLocationOptions: any = {
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 30000,
  };

  constructor() {}

  startSearchingGeoLocationDevice() {
    const state: GeoLocationState = new GeoLocationState();
    if (DotNetBridge.hasGeoLocation) {
      DotNetBridge.geoLocation.getIsAvailable().then((_) => {
        state.deviceAvailable = _;
        this.geoLocationStateSubject.next(state);
        DotNetBridge.geoLocation.onStateChanged(() => {
          DotNetBridge.geoLocation.getIsAvailable().then((av) => {
            state.deviceAvailable = av;
            this.geoLocationStateSubject.next(state);
          });
        });
      });
    } else {
      this.geoLocation = new ol.Geolocation({
        trackingOptions: this.geoLocationOptions,
      });
      this.geoLocationPositionChangeEventKey = this.geoLocation.on('change', () => {
        state.deviceAvailable = true;
        this.geoLocation.setTracking(false);
        this.unbindGeoLocationPositionChangeEvent();
        this.geoLocationStateSubject.next(state);
      });

      this.geoLocation.on('error', (error) => {});

      this.geoLocation.setTracking(true);
    }
  }

  markCoordinate(latOrX: number, lonOrY: number, isProjected: boolean = false) {
    this.raiseMapAction({
      actionId: MapActionIds.MARK_COORDINATE,
      feature: null,
      isFirstConstructionAction: false,
      payload: { latOrX, lonOrY, isProjected },
    });
  }

  clearCoordinateMarkers() {
    this.raiseMapAction({
      actionId: MapActionIds.CLEAR_MARKERS,
      feature: null,
      isFirstConstructionAction: false,
    });
  }

  clearSelections() {
    this.raiseMapAction({
      actionId: MapActionIds.CLEAR_SELECTIONS,
      feature: null,
      isFirstConstructionAction: false,
    });
  }

  startGeoLocationTracking() {
    if (DotNetBridge.hasGeoLocation) {
      DotNetBridge.geoLocation.onPositionChanged((coordinates) => {
        const pos = new GeoLocationPosition();
        pos.longitude = coordinates[0];
        pos.latitude = coordinates[1];
        if (coordinates.length > 1) {
          pos.altitude = coordinates[2];
        }
        this.geoLocationPositionSubject.next(pos);
      });
      DotNetBridge.geoLocation.setEnabled(true);
    } else {
      this.geoLocationPositionChangeEventKey = this.geoLocation.on('change', () => {
        const coordinates = this.geoLocation.getPosition();
        const pos = new GeoLocationPosition();
        pos.longitude = coordinates[0];
        pos.latitude = coordinates[1];
        pos.altitude = this.geoLocation.getAltitude();
        this.geoLocationPositionSubject.next(pos);
      });

      this.geoLocation.on('error', (error) => {
        this.geoLocation.setTracking(false);
        this.geoLocation.setTracking(true);
      });

      this.geoLocation.setTracking(true);
    }

    const state: GeoLocationState = new GeoLocationState();
    state.deviceAvailable = true;
    state.trackingActive = true;
    this.geoLocationStateSubject.next(state);
  }

  stopGeoLocationTracking() {
    if (DotNetBridge.hasGeoLocation) {
      DotNetBridge.geoLocation.setEnabled(false);
    } else {
      this.geoLocation.setTracking(false);
      this.unbindGeoLocationPositionChangeEvent();
    }

    const state: GeoLocationState = new GeoLocationState();
    state.deviceAvailable = true;
    state.trackingActive = false;
    this.geoLocationStateSubject.next(state);
  }

  private unbindGeoLocationPositionChangeEvent() {
    if (this.geoLocationPositionChangeEventKey) {
      ol.Observable.unByKey(this.geoLocationPositionChangeEventKey);
    }
  }

  onGeoLocationStateChanged(): Observable<GeoLocationState> {
    return this.geoLocationStateSubject.asObservable();
  }

  onGeoLocationPositionChanged(): Observable<GeoLocationPosition> {
    return this.geoLocationPositionSubject.asObservable();
  }

  raiseMapAction(mapAction: MapActionArgument) {
    this.mapActionSubject.next(mapAction);
  }

  onMapAction(): Observable<MapActionArgument> {
    return this.mapActionSubject.asObservable();
  }

  registerMapSelectionAction(mapSelectionAction: (selection: DataEntity[]) => void) {
    this.mapSelectEntitiesSubject.next(mapSelectionAction);
  }

  unregisterMapSelectionAction() {
    this.mapSelectEntitiesSubject.next(undefined);
  }

  onMapSelectionActionChanged(): Observable<(action: DataEntity[]) => void> {
    return this.mapSelectEntitiesSubject.asObservable();
  }

  registerMapSelectEntitiesDefaultAction(mapSelectionAction: (selection: DataEntity[]) => void) {
    this.mapSelectEntitiesDefaultSubject.next(mapSelectionAction);
  }

  unregisterMapSelectEntitiesDefaultAction() {
    this.mapSelectEntitiesDefaultSubject.next(undefined);
  }

  onMapSelectEntitiesDefaultActionChanged(): Observable<(action: DataEntity[]) => void> {
    return this.mapSelectEntitiesDefaultSubject.asObservable();
  }

  selectNetworkPath(startObject: DataEntity, includeNodes: boolean) {
    this.pathInteractionSubject.next({
      entity: startObject,
      includeNodes: includeNodes,
    });
  }

  onStartNetworkPathSelection(): Observable<{
    entity: DataEntity;
    includeNodes: boolean;
  }> {
    return this.pathInteractionSubject.asObservable();
  }

  raiseNetworkPathSelected(dataEntities: DataEntity[]) {
    this.networkPathSubject.next(dataEntities);
  }

  onNetworkPathSelected(): Observable<DataEntity[]> {
    return this.networkPathSubject.asObservable();
  }

  networkTracing(url: string) {
    this.networkTracingSubject.next(url);
  }

  onNetworkTracing(): Observable<string> {
    return this.networkTracingSubject.asObservable();
  }

  setRotationEnabled(enabled: boolean) {
    this.enableRotationSubject.next(enabled);
  }

  onRotationEnabled(): Observable<boolean> {
    return this.enableRotationSubject.asObservable();
  }

  raiseMapInitiated() {
    this.mapInitiatedSubject.next(null);
  }

  onMapInitiated() {
    return this.mapInitiatedSubject.asObservable();
  }

  raiseMapLoaded(mapSelection: MapSelection) {
    this.mapLoadedSubject.next(mapSelection);
  }

  notifyLegendInitialized() {
    this.isLegendInitialized = true;
    this.legendInitializedSubject.complete();
  }

  ensureLegendInitialized(): Promise<void> {
    if (this.isLegendInitialized) {
      return Promise.resolve();
    } else {
      return this.legendInitializedSubject.asObservable().toPromise();
    }
  }

  onMapLoaded(): Observable<MapSelection> {
    return this.mapLoadedSubject.asObservable();
  }

  navigateToObjects(objectInfos: DataEntity[]) {
    this.navigationSubject.next(objectInfos);
  }

  navigateToDataRows(rows: DataRowInfo[]) {
    this.navigateToDataRowsSubject.next(rows);
  }

  navigateToObjectsWithoutZoom(objectInfos: DataEntity[]) {
    this.navigationSubjectWithoutZoom.next(objectInfos);
  }

  mapZoomToLayerView(layerItemNode: LayerItemFlatNode) {
    this.mapToLayerSubject.next(layerItemNode);
  }

  mapZoomToLayer(): Observable<LayerItemFlatNode> {
    return this.mapToLayerSubject.asObservable();
  }

  mapOpenDataTable(layerItemNode: LayerItemFlatNode) {
    this.mapOpenDataTableSubject.next(layerItemNode);
  }

  onMapOpenDataTable(): Observable<LayerItemFlatNode> {
    return this.mapOpenDataTableSubject.asObservable();
  }

  onObjectNavigation(): Observable<DataEntity[]> {
    return this.navigationSubject.asObservable();
  }

  onDataRowNavigation(): Observable<DataRowInfo[]> {
    return this.navigateToDataRowsSubject.asObservable();
  }

  onObjectNavigationWithoutZoom(): Observable<DataEntity[]> {
    return this.navigationSubjectWithoutZoom.asObservable();
  }

  refreshMapLayer(objectInfos: DataEntity[]) {
    this.mapRefreshSubject.next(objectInfos);
  }

  onRefreshLayerRequested(): Observable<DataEntity[]> {
    return this.mapRefreshSubject.asObservable();
  }

  setCustomColor(objectInfos: DataEntity[], color: number[]) {
    this.setCustomColorSubject.next({ entities: objectInfos, color: color });
  }

  onSetCustomColor(): Observable<{ entities: DataEntity[]; color: number[] }> {
    return this.setCustomColorSubject.asObservable();
  }

  loadMapConfig(mapConfig: MapConfiguration) {
    this.loadMapConfigSubject.next(mapConfig);
  }

  onMapConfigReceived(): Observable<MapConfiguration> {
    return this.loadMapConfigSubject.asObservable();
  }

  loadContextMenuConfig(menuConfig: ContextMenuConfiguration) {
    this.loadContextMenuConfigSubject.next(menuConfig);
  }

  onContextMenuConfigReceived(): Observable<ContextMenuConfiguration> {
    return this.loadContextMenuConfigSubject.asObservable();
  }

  sendMapShortMessage(message: string) {
    this.mapShortMessageSubject.next(message);
  }
  onMapShortMessageReceived(): Observable<string> {
    return this.mapShortMessageSubject.asObservable();
  }

  raiseExtentSelected(extent: number[]) {
    this.extentSubject.next(extent);
  }

  onExtentSelected(): Observable<number[]> {
    return this.extentSubject.asObservable();
  }

  selectExtent() {
    this.extentInteractionSubject.next(null);
  }

  onStartExtentSelection() {
    return this.extentInteractionSubject.asObservable();
  }

  setIsMobileEnvironment(isMobileEnvironment: boolean): void {
    this.isMobileEnvironmentSubject.next(isMobileEnvironment);
  }

  onIsMobileEnvironment(): Observable<boolean> {
    return this.isMobileEnvironmentSubject.asObservable();
  }

  setFeatureSelectionCanceled(isFeatureSelectionCanceled: boolean): void {
    this.isFeatureSelectionCanceled.next(isFeatureSelectionCanceled);
  }

  onFeatureSelectionCanceled(): Observable<boolean> {
    return this.isFeatureSelectionCanceled.asObservable();
  }

  setMobileEnvironment(isMobileEnvironment: boolean): void {
    this.mobileEnvironmentSubject.next(isMobileEnvironment);
  }

  onMobileEnvironment(): Observable<boolean> {
    return this.mobileEnvironmentSubject.asObservable();
  }

  public getZValue(z_coord: any): number | undefined {
    if (!z_coord) {
      return undefined;
    }

    if (typeof z_coord === 'number' && Number.isFinite(z_coord)) {
      return z_coord;
    }

    if (typeof z_coord === 'string') {
      return Number.parseFloat(z_coord.split(' ')[0].replace(',', '.'));
    }
    return ConvertHelper.convertObject(z_coord);
  }
}
